import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ChevronRight16Regular, ChevronLeft16Regular } from '@fluentui/react-icons';
import { List } from '@fluentui/react-list-preview';
import { FolderAssetType, handleRealUsedFlag, RealUsedFlagSuffix } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { ImageAssetCard } from './home-page-asset-cards/image-asset-card';
import { FolderAssetCard } from './home-page-asset-cards/folder-asset-card';
import { VideoAssetCard } from './home-page-asset-cards/video-asset-card';
import { getAssetListBarCarouselStyle } from '../home-page-styles';

export const AssetCard = ({
  item,
  i18n,
  navigateIntoFolderItem,
  navigation,
  logger,
  renameAsset,
  ariaSetsize,
  ariaPosinset,
  adsStudioAccountPreferences,
}) => {
  const { folderAssetType } = item || {};

  const onClickCard = useCallback(() => {
    handleRealUsedFlag(adsStudioAccountPreferences, RealUsedFlagSuffix.App);
  }, [adsStudioAccountPreferences]);

  switch (folderAssetType) {
    case FolderAssetType.Folder:
      return (
        <FolderAssetCard
          key={item.folderId}
          folder={item}
          i18n={i18n}
          navigateIntoFolderItem={navigateIntoFolderItem}
          navigation={navigation}
          ariaSetsize={ariaSetsize}
          ariaPosinset={ariaPosinset}
          onClickCard={onClickCard}
        />);
    case FolderAssetType.Image:
      return (
        <ImageAssetCard
          key={item.id}
          asset={item}
          i18n={i18n}
          navigation={navigation}
          logger={logger}
          ariaPosinset={ariaPosinset}
          ariaSetsize={ariaSetsize}
          onClickCard={onClickCard}
        />);
    case FolderAssetType.Video:
      return (
        <VideoAssetCard
          key={item.id}
          asset={item}
          i18n={i18n}
          renameAsset={renameAsset}
          navigation={navigation}
          ariaPosinset={ariaPosinset}
          ariaSetsize={ariaSetsize}
          onClickCard={onClickCard}
        />);
    default:
      return null;
  }
};

AssetCard.propTypes = {
  item: PropTypes.shape({
    folderAssetType: PropTypes.string,
    folderId: PropTypes.number,
    id: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  navigateIntoFolderItem: PropTypes.func.isRequired,
  navigation: PropTypes.shape({}).isRequired,
  renameAsset: PropTypes.func.isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
};

const ListBarCarousel = ({
  assets, i18n, navigateIntoFolderItem, renameAsset, navigation, logger, adsStudioAccountPreferences,
}) => {
  const styles = getAssetListBarCarouselStyle();
  const sliderRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [showArrowLeft, setShowArrowLeft] = useState(false);
  const [showArrowRight, setShowArrowRight] = useState(true);

  const computeMaxScrollWidth = useCallback(() => {
    if (sliderRef.current) {
      return sliderRef.current.scrollWidth - sliderRef.current.clientWidth;
    }
    return 0;
  }, []);

  const goLeft = useCallback(() => {
    const newPosition = Math.max(scrollPosition - 960, 0);
    setScrollPosition(newPosition);
  }, [scrollPosition]);

  const goRight = useCallback(() => {
    const maxScroll = computeMaxScrollWidth();
    const newPosition = Math.min(scrollPosition + 960, maxScroll);
    setScrollPosition(newPosition);
  }, [scrollPosition, computeMaxScrollWidth]);

  const isHideSection = useMemo(() => {
    if ((!assets || !assets.length)) {
      return true;
    }
    return false;
  }, [assets]);

  useEffect(() => {
    const maxScroll = computeMaxScrollWidth();
    setShowArrowLeft(scrollPosition > 0);
    setShowArrowRight(scrollPosition < maxScroll);
  }, [scrollPosition, computeMaxScrollWidth]);

  if (isHideSection) {
    return <></>;
  }

  return (
    <>
      <div className={styles.subHeading} role="heading" aria-level="2">
        {i18n.getString(_TL_('Recent'))}
      </div>
      <div className={styles.paragraph}>{i18n.getString(_TL_('Find recent assets'))}</div>
      <div className={classnames('home-carousel', styles.itemContainer)}>
        {showArrowLeft && (
          <div className={classnames(styles.btnWrapper, styles.leftWrapper)}>
            <ChevronLeft16Regular className={styles.leftBtn} onClick={goLeft} />
          </div>
        )}
        <div ref={sliderRef} className={styles.slider}>
          {assets && assets.length > 0 &&
          <List
            role="list"
            navigationMode="composite"
            className={styles.cardContainer}
            style={{ position: 'relative', left: `${-scrollPosition}px` }}
          >
            {assets.map((item, index) => (
              <AssetCard
                key={item.folderId || item.id}
                item={item}
                i18n={i18n}
                navigateIntoFolderItem={navigateIntoFolderItem}
                navigation={navigation}
                renameAsset={renameAsset}
                logger={logger}
                ariaSetsize={assets.length}
                ariaPosinset={index + 1}
                adsStudioAccountPreferences={adsStudioAccountPreferences}
              />
              ))}
          </List>
          }
        </div>
        {showArrowRight && (
          <>
            <div className={classnames(styles.overlay, styles.rightOverlay)} />
            <div className={classnames(styles.btnWrapper, styles.rightWrapper)}>
              <ChevronRight16Regular className={styles.rightBtn} onClick={goRight} />
            </div>
          </>
        )}
        {showArrowLeft && <div className={classnames(styles.overlay, styles.leftOverlay)} />}
      </div>
    </>
  );
};

ListBarCarousel.propTypes = {
  assets: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  navigateIntoFolderItem: PropTypes.func.isRequired,
  renameAsset: PropTypes.func.isRequired,
  navigation: PropTypes.shape({
    gotoAssetDetails: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
};

export default ListBarCarousel;
